import Vue from 'vue';
import VueRouter from 'vue-router';
import NotFound from './components/NotFound';
import store from './store/store';
import GoogleAnalytics from '@/components/base-components/Analytics/GoogleAnalytics';
import {PAGES_NAMES, PAGES_PATHS} from '@/pages';
import VisaicV3Analytics from '@/components/base-components/Analytics/VisaicV3Analytics';
import {CUSTOM_EVENTS, URL_QUERIES} from '@/const';
const NavigationFailureType = {
  redirected: 2,
  aborted: 4,
  cancelled: 8,
  duplicated: 16,
};

import appRoutes from '@/league/leagueRouters';
import {dispatchCustomEvent} from '@/utils/helpers';
import {onSpaNavigation} from '@/utils/gptAds';
import {
  ATTRIBUTES_GROUPS_ALISA_TO_ID_MAP,
  CATEGORIES_FOR_HOMEPAGE,
  GET_ATTRIBUTES_FOR_HOMEPAGE,
  GET_LOCATIONS_FOR_HOMEPAGE,
  GROUPS_FOR_HOMEPAGE,
  PARTICIPANTS_FOR_HOMEPAGE,
} from '@/store/getterTypes';

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => {
    if (
      ![
        NavigationFailureType.duplicated,
        NavigationFailureType.cancelled,
      ].includes(err.type)
    ) {
      return err;
    }
  });
};

const originalReplace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch((err) => {
    if (
      ![
        NavigationFailureType.duplicated,
        NavigationFailureType.cancelled,
      ].includes(err.type)
    ) {
      return err;
    }
  });
};


Vue.use(VueRouter);

const router = new VueRouter({
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    return savedPosition ?? {x: 0, y: 0};
  },
  routes: [
    {
      ...PAGES_PATHS.LOGIN,
      meta: {
        title: 'Login',
        disabledForAuth: true,
        doNotSave: true,
        isAuthPage: true,
      },
      component: () => import(
          /* webpackChunkName: "auth-view" */ './views/LoginScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.REGISTRATION,
      meta: {
        title: 'Registration',
        disabledForAuth: true,
        doNotSave: true,
        isAuthPage: true,
      },
      component: () => import(
          /* webpackChunkName: "auth-view" */ './views/RegistrationScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.VERIFY_NOTICE,
      meta: {
        title: 'Verify notice',
        doNotSave: true,
      },
      component: () => import(
          /* webpackChunkName: "auth-view" */ './views/VerifyNoticeScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.VERIFY_EMAIL,
      meta: {
        title: 'Verify',
        doNotSave: true,
      },
      component: () => import(
          /* webpackChunkName: "auth-view" */ './views/VerifyScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.PASSWORD_RESET_REQUEST,
      meta: {
        title: 'Password reset request',
        disabledForAuth: true,
        doNotSave: true,
      },
      component: () => import(
          // eslint-disable-next-line max-len
          /* webpackChunkName: "auth-view" */ './views/PasswordResetRequestScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.PASSWORD_RESET,
      meta: {
        title: 'Password reset',
        disabledForAuth: true,
        doNotSave: true,
      },
      component: () => import(
          /* webpackChunkName: "auth-view" */ './views/PasswordResetScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.PROFILE,
      meta: {
        title: 'Profile',
      },
      component: () => import(/* webpackChunkName: "profile-view" */ './views/ProfileScreen.vue'),
      children: [
        {
          ...PAGES_PATHS.ORDER_LIST,
          component: () => import(
              /* webpackChunkName: "profile-view" */ './components/profile/OrdersList.vue'),
          children: [
            {
              ...PAGES_PATHS.ACTIVE_ORDERS,
              component: () => import(
                  /* webpackChunkName: "profile-view" */ './components/profile/ActiveOrders.vue'),
            },
            {
              ...PAGES_PATHS.TRANSACTIONS,
              component: () => import(
                  /* webpackChunkName: "profile-view" */
                  './components/profile/HistoryOrdersList.vue'
              ),
            },
            {
              ...PAGES_PATHS.LINK_DEVICE,
              component: () => import(
                  /* webpackChunkName: "profile-view" */ './components/profile/LinkDevice.vue'),
            },
          ],
        },
        {
          ...PAGES_PATHS.PROFILE_EDIT,
          meta: {
            title: 'Update profile',
          },
          beforeEnter: (to, from, next) => {
            if (store.getters.isLogged) {
              next();
            } else {
              next(PAGES_PATHS.LOGIN);
            }
          },
          component: () => import(
              /* webpackChunkName: "schedule-view" */ './components/profile/UpdateProfileComponent'
          ),
        },
      ],
    },
    {
      ...PAGES_PATHS.CONTACTS,
      meta: {
        title: 'Contacts',
      },
      component: () => import(
          /* webpackChunkName: "contacts-view" */ './views/ContactScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.BUY,
      meta: {
        title: 'Buy',
        requiresAuth: true,
      },
      component: () => import(
          /* webpackChunkName: "buy-view" */ './views/BuyScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.SELECT_PACKAGE,
      meta: {
        title: 'Select package',
        requiresAuth: true,
      },
      component: () => import(
          /* webpackChunkName: "buy-view" */ './views/SelectPackageScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.FAQ,
      meta: {
        title: 'FAQ',
      },
      component: () => import(
          /* webpackChunkName: "faq-view" */ './views/FaqScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.CLIPS_PAGE,
      meta: {
        title: 'Clips',
        requiresAuth: true,
      },
      component: () => import(/* webpackChunkName:"clips-view" */ './league/views/ClipsScreen.vue'),
    },
    {
      ...PAGES_PATHS.SIMULATE,
      meta: {
        title: 'Simulate',
      },
      component: () => import(
          /* webpackChunkName: "simulate-view" */ './views/SimulateScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.CONFIRM_ACCOUNT_DELETION,
      meta: {
        title: PAGES_NAMES.CONFIRM_ACCOUNT_DELETION,
      },
      component: () => import(
          /* webpackChunkName: "auth-view" */ './views/ConfirmAccountDeletion.vue'
      ),
    },
    {
      ...PAGES_PATHS.PARTICIPANTS,
      meta: {
        title: PAGES_NAMES.PARTICIPANTS,
        getterName: PARTICIPANTS_FOR_HOMEPAGE,
      },
      component: () => import('./league/views/LinkItemsScreen.vue'),
    },
    {
      ...PAGES_PATHS.CATEGORIES,
      meta: {
        title: PAGES_NAMES.CATEGORIES,
        getterName: CATEGORIES_FOR_HOMEPAGE,
      },
      component: () => import('./league/views/LinkItemsScreen.vue'),
    },
    {
      ...PAGES_PATHS.GROUPS,
      meta: {
        title: PAGES_NAMES.GROUPS,
        getterName: GROUPS_FOR_HOMEPAGE,
      },
      component: () => import('./league/views/LinkItemsScreen.vue'),
    },
    {
      ...PAGES_PATHS.LOCATIONS,
      meta: {
        title: PAGES_NAMES.LOCATIONS,
        getterName: GET_LOCATIONS_FOR_HOMEPAGE,
      },
      component: () => import('./league/views/LinkItemsScreen.vue'),
    },
    {
      ...PAGES_PATHS.ATTRIBUTES,
      meta: {
        title: PAGES_NAMES.ATTRIBUTES,
        getterName: GET_ATTRIBUTES_FOR_HOMEPAGE,
        getterValueRequired: true,
        getterValue: ATTRIBUTES_GROUPS_ALISA_TO_ID_MAP,
      },
      component: () => import('./league/views/LinkItemsScreen.vue'),
    },
    {
      ...PAGES_PATHS.ERROR,
      meta: {
        title: '404',
        doNotSave: true,
      },
      component: NotFound,
    },
    {
      ...PAGES_PATHS.ABOUT,
      meta: {
        title: 'About us',
      },
      component: () => import(
          /* webpackChunkName: "faq-view" */ './views/AboutUsScreen.vue'
      ),
    },
    {
      ...PAGES_PATHS.MOMENTS,
      meta: {
        title: 'Moments',
      },
      component: () => import(
          /* webpackChunkName: "reels-view" */ './views/MomentsScreen.vue'
      ),
    },
    ...appRoutes,
  ],
});

export const PATH_TO_REDIRECT_IF_LOGGED = '/';

/**
 * @param store: Object
 * @param store.state: Object
 * @param store.state.auth.user.temporary_password: Boolean
 */
router.beforeEach((to, from, next) => {
  Vue.$storage.set('previous_page_for_analytics', `${location.origin}${from.fullPath}`);
  const logged = store.getters.isLogged;

  // to avoid cycling if not logged user tried to buy package
  // but go back from login / registration pages by history navigation without logged in.
  if (!logged && to.meta.requiresAuth && from.meta.isAuthPage) {
    return router.go(-1);
  }

  // todo: add check for unauthorised users to avoid navigation to requiresAuth pages
  const hasTempPass = store.state?.auth?.user?.temporary_password;
  if (logged && hasTempPass && (PAGES_PATHS.PROFILE_EDIT.path !== to.path)) {
    if (PAGES_PATHS.PROFILE_EDIT.path === from.path) {
      return false;
    }
    return next(PAGES_PATHS.PROFILE_EDIT);
  } else if (logged && to.meta.disabledForAuth) {
    return next(PATH_TO_REDIRECT_IF_LOGGED);
  } else if (to.meta.doNotSave) {
    return next();
  } else {
    Vue.$storage.set('form_fullPath', from.name ?
        `${location.origin}${from.fullPath}` :
        document.referrer,
    );
    Vue.$storage.set('last_page', to.path);
    Vue.$storage.set('last_page_query', to.query);
    Vue.$storage.set('last_page_params', to.params);
    return next();
  }
});
router.afterEach((to, from) => {
  GoogleAnalytics.pageView({
    page_title: to.meta?.title,
    page_location: `${location.origin}${to.path}`,
    page_path: to.path,
  });

  if (to.query[URL_QUERIES.V3_OUTER_TRACKING]) {
    VisaicV3Analytics.outerTracker = to.query[URL_QUERIES.V3_OUTER_TRACKING];
  }
  VisaicV3Analytics.event({
    event: 'visit_page',
    title: to?.meta.title,
    url: `${location.origin}${to.path}`,
    referrer: `${location.origin}/${from.fullPath}`,
    api_key: Vue.$storage.get('api_key'),
  });

  dispatchCustomEvent(CUSTOM_EVENTS.ROUTE_UPDATED, {
    to: location.href,
    referrer: `${location.origin}/${from.fullPath}`,
  });

  onSpaNavigation(from);
});


router.onError((error) => {
  if (/ChunkLoadError|Loading chunk/i.test(error.message)) {
    dispatchCustomEvent(CUSTOM_EVENTS.APP_UPDATED, error);
  } else {
    dispatchCustomEvent(CUSTOM_EVENTS.ROUTE_ERROR, error);
  }
});

export default router;
